/**
 * ReadAloud — the speaker button Michael asked for (Feb 2026).
 *
 * Contract: tap → the text is spoken in Michael's voice. Cache hits
 * cost £0. First-time text incurs one ElevenLabs synthesis and is
 * cached forever after.
 *
 * The audio blob is cached in-memory per instance so tapping twice
 * doesn't re-fetch. Simultaneous speakers pause each other so only
 * one voice plays at a time (managed by a tiny module-level bus).
 */
import React from "react";
import axios from "axios";
import { Volume2, Loader2, Pause } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem("reclaim_token");

// Module-level "only one speaker at a time" bus.
let ACTIVE_AUDIO = null;
const stopActive = () => {
  if (ACTIVE_AUDIO && !ACTIVE_AUDIO.paused) {
    try { ACTIVE_AUDIO.pause(); } catch { /* noop */ }
  }
};

export default function ReadAloud({ text, size = 14, className = "", testId, tint = "#e7c98a" }) {
  const [state, setState] = React.useState("idle"); // idle | loading | playing | error
  const audioRef = React.useRef(null);
  const cachedUrlRef = React.useRef(null);

  React.useEffect(() => () => {
    // If this instance owns the active audio, release it on unmount.
    if (audioRef.current && ACTIVE_AUDIO === audioRef.current) {
      try { audioRef.current.pause(); } catch { /* noop */ }
      ACTIVE_AUDIO = null;
    }
  }, []);

  const play = async () => {
    if (state === "playing") {
      try { audioRef.current?.pause(); } catch { /* noop */ }
      setState("idle");
      return;
    }
    stopActive();
    let url = cachedUrlRef.current;
    if (!url) {
      setState("loading");
      try {
        const r = await axios.post(
          `${API}/api/read-aloud`,
          { text },
          { headers: { Authorization: `Bearer ${getToken()}` } },
        );
        url = r.data?.audio_url;
        if (!url) throw new Error("no_audio");
        cachedUrlRef.current = url;
      } catch (e) {
        setState("error");
        setTimeout(() => setState("idle"), 1800);
        return;
      }
    }
    const a = new Audio(url);
    audioRef.current = a;
    ACTIVE_AUDIO = a;
    a.onended = () => { setState("idle"); ACTIVE_AUDIO = null; };
    a.onpause = () => { if (state === "playing") setState("idle"); };
    a.onerror = () => { setState("error"); setTimeout(() => setState("idle"), 1800); };
    try {
      await a.play();
      setState("playing");
    } catch {
      setState("error");
      setTimeout(() => setState("idle"), 1800);
    }
  };

  const Icon = state === "loading" ? Loader2 : state === "playing" ? Pause : Volume2;
  return (
    <button
      onClick={(e) => { e.stopPropagation(); play(); }}
      aria-label={state === "playing" ? "Pause read-aloud" : "Read aloud"}
      title="Read aloud in Michael's voice"
      className={`inline-flex items-center justify-center rounded-full transition-all active:scale-95 ${className}`}
      style={{
        width: size + 12,
        height: size + 12,
        background: state === "playing" ? tint : "transparent",
        border: `1px solid ${tint}`,
        color: state === "playing" ? "#0d1f3a" : tint,
        opacity: state === "error" ? 0.55 : 1,
      }}
      data-testid={testId}
    >
      <Icon size={size} className={state === "loading" ? "animate-spin" : ""} />
    </button>
  );
}
